import React, { useState } from "react";
import { Col, Row, Input, Label, FormFeedback, Button } from "reactstrap";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { createCourse, updatecourse } from "slices/Course/thunk";
import { AppDispatch } from "store";

interface CourseFormProps {
  validation: any;
  isEdit?: boolean;
  onSuccessNext?: () => void;
}

const CourseForm: React.FC<CourseFormProps> = ({ validation, isEdit = false, onSuccessNext }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [saving, setSaving] = useState(false);

  const customSubmit = async (e: any) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (saving) {
      return;
    }

    const errors = await validation.validateForm();

    Object.keys(errors).forEach((field) => {
      validation.setFieldTouched(field, true, false);
    });

    if (Object.keys(errors).length > 0) {
      toast.error("Please fill all required fields.");
      return;
    }

    const values = validation.values;

    try {
      setSaving(true);
      if (isEdit && values?.course_id) {
        await dispatch(updatecourse(values.course_id, values));
        toast.success("Course Updated successfully!");
      } else {
        const response = await dispatch(createCourse(values) as any);
        if (response?.data) {
          validation.setFieldValue("course_id", response.data);
        }
        toast.success("Course added successfully!");
      }

      if (onSuccessNext) onSuccessNext();
    } catch (error) {
      console.error("Error saving course:", error);
      toast.error("Failed to save course. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={customSubmit} noValidate>
      <div className="px-3 py-2">
        <Row className="g-3 mt-2">
          <Col md={12}>
            <Label>Course Name *</Label>
            <Input
              name="course_name"
              value={validation.values.course_name}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.course_name && validation.errors.course_name)}
              placeholder="Type course name..."
            />
            <FormFeedback>{validation.errors.course_name}</FormFeedback>
          </Col>

          <Col md={6}>
            <Label>Trainer *</Label>
            <Input
              name="trainer"
              value={validation.values.trainer}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.trainer && validation.errors.trainer)}
              placeholder="Trainer name"
            />
            <FormFeedback>{validation.errors.trainer}</FormFeedback>
          </Col>

          <Col md={6}>
            <Label>Modules *</Label>
            <Input
              name="noofmodule"
              value={validation.values.noofmodule}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.noofmodule && validation.errors.noofmodule)}
              placeholder="No. of modules"
            />
            <FormFeedback>{validation.errors.noofmodule}</FormFeedback>
          </Col>

          <Col md={6}>
            <Label>Hours per day *</Label>
            <Input
              name="hourperday"
              value={validation.values.hourperday}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.hourperday && validation.errors.hourperday)}
              placeholder="12"
            />
            <FormFeedback>{validation.errors.hourperday}</FormFeedback>
          </Col>

          <Col md={6}>
            <Label>Week *</Label>
            <Input
              name="noofweek"
              value={validation.values.noofweek}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.noofweek && validation.errors.noofweek)}
              placeholder="04"
            />
            <FormFeedback>{validation.errors.noofweek}</FormFeedback>
          </Col>
        </Row>
       <Row className="g-3 mt-2">
          <Col md={6}>
            <Label>Cover Image *</Label>
            <Input
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              name="thumbnail_url"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;

                const reader = new FileReader();
                reader.onloadend = () => {
                  const base64 = reader.result;
                  validation.setFieldValue("thumbnail_url", base64);
                };
                reader.readAsDataURL(file);
              }}
              invalid={!!(validation.touched.thumbnail_url && validation.errors.thumbnail_url)}
            />

            <FormFeedback>{validation.errors.thumbnail_url}</FormFeedback>

            {validation.values.thumbnail_url && (
              <div className="mt-2">
                <img
                  src={validation.values.thumbnail_url}
                  alt="preview"
                  style={{ width: "120px", height: "auto", borderRadius: "6px" }}
                />
              </div>
            )}
          </Col>
        </Row>
        <Row className="g-3 mt-2">
          <Col md={6}>
            <Label>Duration *</Label>
            <Input
              name="duration"
              value={validation.values.duration}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.duration && validation.errors.duration)}
              placeholder="e.g. 40 hrs"
            />
            <FormFeedback>{validation.errors.duration}</FormFeedback>
          </Col>

          <Col md={6}>
            <Label>Course Level *</Label>
            <Input
              type="select"
              name="course_level"
              value={validation.values.course_level}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.course_level && validation.errors.course_level)}
            >
              <option value="">Select Level</option>
              <option value="Beginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
            </Input>
            <FormFeedback>{validation.errors.course_level}</FormFeedback>
          </Col>
        </Row>

        <Row className="g-3 mt-3">
          <Col md={6}>
            <Label>Course Price *</Label>
            <Input
              name="price"
              value={validation.values.price}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.price && validation.errors.price)}
              placeholder="Enter price"
            />
            <FormFeedback>{validation.errors.price}</FormFeedback>
          </Col>

          <Col md={6}>
            <Label>Description *</Label>
            <Input
              type="textarea"
              name="description"
              value={validation.values.description}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.description && validation.errors.description)}
              placeholder="Write about the course..."
              rows={3}
            />
            <FormFeedback>{validation.errors.description}</FormFeedback>
          </Col>
        </Row>

        <div className="d-flex justify-content-end mt-4">
          <Button 
            color="primary" 
            type="submit" 
            disabled={saving}
            style={{ 
              minWidth: "150px",
              opacity: saving ? 0.7 : 1,
              cursor: saving ? "not-allowed" : "pointer"
            }}
          >
            {saving ? (
              <>
                <i className="fa fa-spinner fa-spin me-2" />
                {isEdit ? "Updating..." : "Saving..."}
              </>
            ) : (
              isEdit ? "Update" : "Save & Continue"
            )}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CourseForm;
